import type { LoggerDraft } from './loggerDraft';
import {
  hasCommittedWork,
  parsePersistedBlob,
  type PersistedLoggerState,
} from './loggerDraftPersistence';

// web-rnw twin of ./loggerDraftStorage.ts (see that file's header) — same
// key namespace + JSON shape, localStorage instead of AsyncStorage.

// ⭐ Backend scope (INFRA-1 slice B). The mobile mount injects the active
// stage at boot; production and every web mount leave it empty, so the keys
// stay exactly as they have always been — nothing migrates. A dev package
// pointed at another backend keeps that backend's draft apart, because a
// draft resumed against the wrong API would save real sets to the wrong
// place. The stage is injected, never imported: this file is shared code and
// knows nothing about mobile's backend module.
let scope = '';

export function setLoggerDraftScope(stage: string | null): void {
  scope = stage === null || stage === '' ? '' : `.${stage}`;
}

const liveKey = () => `jl.workoutLog.v1${scope}`;

/** Prefix for timestamped quarantine slots. A function, not a const, because
 *  the scope is only known after boot. The stage sits BEFORE `quarantine` so
 *  production's prefix is not also a prefix of another stage's keys — an
 *  enumeration on production must never turn up a lab draft.
 *  Exported so the mobile recovery store
 *  (apps/mobile/src/state/quarantineStore.ts) can enumerate + reload
 *  preserved blobs. */
export const quarantinePrefix = () => `jl.workoutLog${scope}.quarantine.v1.`;

export async function loadPersistedLoggerState(): Promise<PersistedLoggerState | null> {
  try {
    const raw = localStorage.getItem(liveKey());
    if (raw === null) return null;
    return parsePersistedBlob(raw);
  } catch {
    return null;
  }
}

export async function savePersistedLoggerState(
  draft: LoggerDraft,
  open: boolean,
): Promise<void> {
  try {
    const payload: PersistedLoggerState = {
      draft,
      open,
      inProgress: hasCommittedWork(draft),
      lastWrittenAt: new Date().toISOString(),
    };
    localStorage.setItem(liveKey(), JSON.stringify(payload));
  } catch {
    // Best-effort; storage failures shouldn't break the logger.
  }
}

export async function clearPersistedLoggerState(): Promise<void> {
  try {
    localStorage.removeItem(liveKey());
  } catch {
    // Best-effort.
  }
}

export async function quarantinePersistedLoggerState(): Promise<string | null> {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(liveKey());
  } catch {
    raw = null;
  }
  if (raw !== null) {
    const key = `${quarantinePrefix()}${new Date().toISOString()}`;
    try {
      localStorage.setItem(key, raw);
    } catch {
      // If quarantine write fails, still proceed to remove the live
      // blob — the user explicitly asked to discard.
    }
  }
  try {
    localStorage.removeItem(liveKey());
  } catch {
    // Best-effort.
  }
  return raw;
}
